import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { FaBars, FaTimes, FaChevronDown } from "react-icons/fa";
import "./Header.css";
import logoImg from "../assets/logo.webp";

const serviceLinks = [
  { path: "/services/website-design", label: "Website Design" },
  { path: "/services/web-development", label: "Web Development" },
  { path: "/services/wordpress-development", label: "WordPress Development" },
  {
    path: "/services/ecommerce-website-development",
    label: "E-Commerce Solutions",
  },
  { path: "/services/seo-services", label: "SEO Services" },
  { path: "/services/packaging-design", label: "Packaging Design" },
];

const navLinks = [
  { path: "/", label: "Home" },
  { path: "/about", label: "About Us" },
  { path: "/services", label: "Services", children: serviceLinks },
  { path: "/projects", label: "Projects" },
  { path: "/blogs", label: "Blogs" },
  { path: "/contact", label: "Contact Us" },
];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      <header className={`header${scrolled ? " header--scrolled" : ""}`}>
        <div className="header__container">
          <Link
            to="/"
            className="header__logo"
            aria-label="Amresh Prajapati Home"
            title="Amresh Prajapati"
          >
            <img
              src={logoImg}
              alt="Amresh Prajapati - Freelance Website Developer in Mumbai"
              width="160"
              height="60"
            />
          </Link>

          <nav className="header__nav" aria-label="Primary navigation">
            {navLinks.map((link) =>
              link.children ? (
                <div
                  key={link.path}
                  className="header__nav-item header__nav-item--dropdown"
                >
                  <NavLink
                    to={link.path}
                    end={link.path === "/"}
                    className={({ isActive }) =>
                      `header__link header__link--dropdown${
                        isActive ? " header__link--active" : ""
                      }`
                    }
                  >
                    {link.label}
                    <FaChevronDown className="header__caret" aria-hidden="true" />
                  </NavLink>

                  <div className="header__dropdown" role="menu">
                    {link.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        role="menuitem"
                        className={({ isActive }) =>
                          `header__dropdown-link${
                            isActive ? " header__dropdown-link--active" : ""
                          }`
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={link.path}
                  to={link.path}
                  end={link.path === "/"}
                  className={({ isActive }) =>
                    `header__link${isActive ? " header__link--active" : ""}`
                  }
                >
                  {link.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="header__right">
            <Link
              to="/contact"
              className="header__cta"
              title="Hire Amresh Prajapati"
            >
              Hire Me
            </Link>

            <button
              type="button"
              className="header__burger"
              onClick={() => setMenuOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={menuOpen}
              aria-controls="mobile-navigation"
            >
              <FaBars />
            </button>
          </div>
        </div>
      </header>

      <div
        className={`header__overlay${menuOpen ? " header__overlay--visible" : ""}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      <aside
        id="mobile-navigation"
        className={`header__mobile${menuOpen ? " header__mobile--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation menu"
      >
        <div className="header__mobile-header">
          <span className="header__mobile-brand">Amresh Prajapati</span>

          <button
            type="button"
            className="header__close"
            onClick={() => setMenuOpen(false)}
            aria-label="Close navigation menu"
          >
            <FaTimes />
          </button>
        </div>

        <div className="header__mobile-divider" />

        <nav className="header__mobile-nav" aria-label="Mobile navigation">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.path} className="header__mobile-item">
                <button
                  type="button"
                  className={`header__mobile-link header__mobile-link--toggle${
                    servicesOpen ? " header__mobile-link--expanded" : ""
                  }`}
                  onClick={() => setServicesOpen((v) => !v)}
                  aria-expanded={servicesOpen}
                >
                  <span>{link.label}</span>
                  <FaChevronDown
                    className="header__mobile-caret"
                    aria-hidden="true"
                  />
                </button>

                <div
                  className={`header__mobile-sub${
                    servicesOpen ? " header__mobile-sub--open" : ""
                  }`}
                >
                  <div className="header__mobile-sub-inner">
                    {link.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        className={({ isActive }) =>
                          `header__mobile-sublink${
                            isActive ? " header__mobile-sublink--active" : ""
                          }`
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                    <NavLink
                      to={link.path}
                      className="header__mobile-sublink header__mobile-sublink--all"
                    >
                      View All Services
                    </NavLink>
                  </div>
                </div>
              </div>
            ) : (
              <div key={link.path} className="header__mobile-item">
                <NavLink
                  to={link.path}
                  end={link.path === "/"}
                  className={({ isActive }) =>
                    `header__mobile-link${
                      isActive ? " header__mobile-link--active" : ""
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              </div>
            )
          )}
        </nav>

        <div className="header__mobile-footer">
          <Link to="/contact" className="header__mobile-cta">
            Hire Amresh Prajapati
          </Link>
        </div>
      </aside>
    </>
  );
};

export default Header;